import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Download, Loader2, Search, Calendar, User, Building2, Package, Mail, RefreshCw, X, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyQuotes = () => {
    const { token } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [filteredQuotes, setFilteredQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('sent');
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [resending, setResending] = useState(null);

    useEffect(() => {
        fetchQuotes();
    }, []);

    useEffect(() => {
        filterQuotes();
    }, [quotes, searchTerm, statusFilter]);

    const fetchQuotes = async () => {
        try {
            const response = await axios.get(`${API}/quotes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQuotes(response.data);
        } catch (error) {
            toast.error('Failed to load quotes');
        } finally {
            setLoading(false);
        }
    };

    const filterQuotes = () => {
        let filtered = [...quotes];
        
        // Filter by status
        if (statusFilter !== 'all') {
            filtered = filtered.filter(q => q.status === statusFilter);
        }
        
        // Filter by search term
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(q => 
                q.reference_number?.toLowerCase().includes(search) ||
                q.customer?.company_name?.toLowerCase().includes(search) ||
                q.customer?.contact_name?.toLowerCase().includes(search) ||
                q.customer?.name?.toLowerCase().includes(search) ||
                q.customer?.email?.toLowerCase().includes(search) ||
                q.spare_pack?.name?.toLowerCase().includes(search)
            );
        }
        
        setFilteredQuotes(filtered);
    };

    const handleDownloadPdf = async (quoteId) => {
        try {
            const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `quote-${quoteId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download PDF');
        }
    };

    const handleResend = async (quoteId, e) => {
        e.stopPropagation();
        setResending(quoteId);
        try {
            const response = await axios.post(`${API}/quotes/${quoteId}/resend`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message || 'Quote resent successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend quote');
        } finally {
            setResending(null);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            sent: 'bg-blue-100 text-blue-800',
            accepted: 'bg-green-100 text-green-800',
            declined: 'bg-red-100 text-red-800',
            expired: 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-lg uppercase ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getContactName = (customer) => {
        return customer?.contact_name || customer?.name || 'Unknown';
    };

    const getCompanyName = (customer) => {
        return customer?.company_name || customer?.name || 'Unknown';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            <div className="mb-6">
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">
                    My Quotes
                </h1>
                <p className="text-brand-gray-500 mt-1">
                    View and manage your submitted quotes
                </p>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                    <Input
                        type="text"
                        placeholder="Search by reference, company, contact, or pack..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-12 pl-10"
                        data-testid="quotes-search-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-40 h-12" data-testid="status-filter-select">
                        <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {filteredQuotes.length === 0 ? (
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardContent className="p-12 text-center">
                        <FileText className="h-16 w-16 text-brand-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold font-heading text-brand-charcoal mb-2">
                            {quotes.length === 0 ? 'No Quotes Yet' : 'No Quotes Found'}
                        </h3>
                        <p className="text-brand-gray-500">
                            {quotes.length === 0 
                                ? 'Create your first quote to get started'
                                : 'Try adjusting your search or filter criteria'
                            }
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {filteredQuotes.map((quote) => (
                        <Card 
                            key={quote.id} 
                            className="border-0 shadow-lg rounded-lg cursor-pointer hover:shadow-xl transition-shadow"
                            onClick={() => setSelectedQuote(quote)}
                            data-testid={`quote-card-${quote.id}`}
                        >
                            <CardContent className="p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="font-bold font-heading text-brand-charcoal">
                                                {quote.reference_number}
                                            </span>
                                            {getStatusBadge(quote.status)}
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm text-brand-gray-600">
                                                <Building2 className="h-4 w-4 text-brand-gray-400 flex-shrink-0" />
                                                <span className="truncate">{getCompanyName(quote.customer)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-brand-gray-600">
                                                <User className="h-4 w-4 text-brand-gray-400 flex-shrink-0" />
                                                <span className="truncate">{getContactName(quote.customer)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-brand-gray-500">
                                                <Package className="h-4 w-4 text-brand-gray-400 flex-shrink-0" />
                                                <span className="truncate">
                                                    {quote.items?.length > 1
                                                        ? `${quote.items.length} packs`
                                                        : quote.items?.[0]?.pack_name || quote.spare_pack?.name}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <span className="text-xl font-bold font-heading text-brand-lime">
                                            £{quote.total_price?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                        </span>
                                        <div className="flex items-center gap-2 text-xs text-brand-gray-500">
                                            <Calendar className="h-3 w-3" />
                                            {formatDate(quote.created_at)}
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadPdf(quote.id);
                                                }}
                                                className="h-9"
                                                data-testid={`download-pdf-${quote.id}`}
                                            >
                                                <Download className="h-4 w-4 mr-1" />
                                                PDF
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => handleResend(quote.id, e)}
                                                disabled={resending === quote.id}
                                                className="h-9"
                                                data-testid={`resend-${quote.id}`}
                                            >
                                                {resending === quote.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <RefreshCw className="h-4 w-4 mr-1" />
                                                        Resend
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Quote Detail Modal */}
            <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>Quote Details</span>
                            {selectedQuote && getStatusBadge(selectedQuote.status)}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedQuote && (
                        <div className="space-y-6 py-4">
                            {/* Reference */}
                            <div className="text-center py-4 bg-brand-gray-50 rounded-lg">
                                <span className="text-xs font-bold uppercase tracking-wide text-brand-gray-500">
                                    Quote Reference
                                </span>
                                <p className="text-2xl font-bold font-heading text-brand-charcoal mt-1">
                                    {selectedQuote.reference_number}
                                </p>
                                <p className="text-sm text-brand-gray-500 mt-1">
                                    {formatDate(selectedQuote.created_at)}
                                </p>
                            </div>

                            {/* Customer Details */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">
                                    Customer
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3 text-sm">
                                        <Building2 className="h-4 w-4 text-brand-gray-400" />
                                        <span className="text-brand-charcoal font-medium">
                                            {getCompanyName(selectedQuote.customer)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                        <User className="h-4 w-4 text-brand-gray-400" />
                                        <span className="text-brand-gray-600">
                                            {getContactName(selectedQuote.customer)}
                                            {selectedQuote.customer?.contact_job_title && (
                                                <span className="text-brand-gray-400 ml-1">
                                                    ({selectedQuote.customer.contact_job_title})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                        <Mail className="h-4 w-4 text-brand-gray-400" />
                                        <span className="text-brand-gray-600">
                                            {selectedQuote.customer?.email}
                                        </span>
                                    </div>
                                    <div className="flex items-start gap-3 text-sm">
                                        <MapPin className="h-4 w-4 text-brand-gray-400 flex-shrink-0 mt-0.5" />
                                        <span className="text-brand-gray-600">
                                            {selectedQuote.customer?.address}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Pack Details */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">
                                    {selectedQuote.items?.length > 1 ? 'Spares Packs' : 'Spares Pack'}
                                </h4>
                                <div className="space-y-2">
                                    {(selectedQuote.items && selectedQuote.items.length > 0) ? (
                                        selectedQuote.items.map((item) => (
                                            <div key={item.pack_id} className="p-3 bg-brand-gray-50 rounded-lg flex items-center justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-brand-charcoal text-sm">{item.pack_name}</p>
                                                    <p className="text-xs text-brand-gray-500">
                                                        Qty: {item.quantity} x £{item.unit_price?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                                        {item.vat_rate > 0 && <span className="ml-1 text-brand-gray-400">({item.vat_rate_name} {item.vat_rate}%)</span>}
                                                    </p>
                                                </div>
                                                <span className="text-sm font-bold whitespace-nowrap">
                                                    £{item.line_total_ex_vat?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4 bg-brand-gray-50 rounded-lg">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-medium text-brand-charcoal">{selectedQuote.spare_pack?.name}</p>
                                                    {selectedQuote.spare_pack?.description && (
                                                        <p className="text-sm text-brand-gray-500 mt-1">{selectedQuote.spare_pack.description}</p>
                                                    )}
                                                </div>
                                                <span className="text-lg font-bold font-heading text-brand-charcoal">
                                                    £{selectedQuote.total_price?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Totals */}
                                <div className="pt-2 border-t border-brand-gray-200 space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-brand-gray-500">Subtotal (Ex VAT)</span>
                                        <span className="font-medium">£{(selectedQuote.total_ex_vat || selectedQuote.total_price)?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-brand-gray-500">VAT</span>
                                        <span className="font-medium">£{(selectedQuote.total_vat || selectedQuote.vat_amount || 0)?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="flex justify-between pt-1 border-t border-brand-gray-200">
                                        <span className="font-bold text-brand-charcoal">Total (Inc VAT)</span>
                                        <span className="text-xl font-bold font-heading text-brand-lime">
                                            £{(selectedQuote.total_inc_vat || selectedQuote.total_price)?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => handleDownloadPdf(selectedQuote.id)}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download PDF
                                </Button>
                                <Button
                                    className="flex-1 bg-[#7AC943] hover:bg-[#68B035] text-black"
                                    onClick={(e) => {
                                        handleResend(selectedQuote.id, e);
                                        setSelectedQuote(null);
                                    }}
                                    disabled={resending === selectedQuote.id}
                                >
                                    {resending === selectedQuote.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                    )}
                                    Resend Quote
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MyQuotes;
